import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SuperAdminService } from '@core/services/super-admin.service';
import { CancelModalComponent } from '../cancel-modal/cancel-modal.component';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { CommonFunctions } from '@shared/common-functions';
import { ToastService } from '@shared/services/toast.service';
import { DriverService } from '../driver/services/driver.service';
import {
  CANCEL_BOOKING_CONFIRMATION,
  CANCEL_INVOICE_CONFIRMATION,
  CUSTOM_INVOICE,
  MARK_AS_PAID_CONFIRMATION,
  PAYMENT_MODE,
  USER_ROLES,
} from '@shared/constant/shared.constant';
import {
  BreadcrumbItem,
  CompanyService,
} from '@shared/services/company.service';
import {
  InvoicePaymentStatus,
  OfflineBookingPaymentAction,
  PaymentMode,
  PaymentStatusForMessage,
} from '@core/models/api-response.model';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { BookingFlowService } from '../booking-flow/service/booking-flow.service';
import { PayRepayModalComponent } from '../pay-repay-modal/pay-repay-modal.component';
import { PaymentStatus } from '@core/models/booking.model';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
@Component({
  selector: 'app-revenue-collection-dashboard',
  templateUrl: './revenue-collection-dashboard.component.html',
  styleUrls: ['./revenue-collection-dashboard.component.scss'],
})
export class RevenueCollectionDashboardComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  selectPaymentStatus: { name: string; value: PaymentStatus }[] = [];
  selectedProducts!: any[];
  revenueFilterForm!: FormGroup;
  revenueCollectionData: any = [];
  selectType: { name: string; value: number }[] = [];
  totalRecords: number;
  itemsPerPage: number = 10;
  first: number = 0;
  items: MenuItem[] | undefined;
  activeItem: any;
  companyId: string = null;
  parkingLotId: string = '';
  companyIdLocalStroage:string = '';
  excelFile: any[];
  parkingLotName: string;
  role: string = '';
  roleRoute: string = '';
  startDate: string;
  endDate: string;
  pageNumber: number = 1;
  searchFilterData: any = {
    name: '',
    invoiceNumber: '',
    email: '',
    type: 0,
    paymentStatus: 0,
  };
  offlineBookingPaymentAction = OfflineBookingPaymentAction;
  invoicePaymentStatus = InvoicePaymentStatus;
  paymentStatusForMessage = PaymentStatusForMessage;

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly superAdminService: SuperAdminService,
    private readonly dialogControlService: DialogControlService,
    private readonly toastService: ToastService,
    private readonly sharedDriverService: DriverService,
    private readonly companyService: CompanyService,
    private readonly bookingFlowService: BookingFlowService,
    private readonly  confirmationService: ConfirmationService,
    private readonly encryptStorage: EncryptionStorageService
  ) {}

  ngOnInit(): void {
    this.startDate = history.state.startDate;
    this.endDate = history.state.endDate;
    this.parkingLotName = history.state.rowData.parkingLotName;
    this.parkingLotId = history.state.rowData.parkingLotId;
    this.companyId = history.state.rowData.companyId;
    this.role = this.encryptStorage.getItem('roleName');
    this.companyIdLocalStroage = this.encryptStorage.getItem('companyId');
    this.roleRoute = CommonFunctions.routeToBookingDetail(this.role);
    this.revenueDashboardBreadcrumbs();
    this.revenueFilterForm = this.fb.group({
      name: [''],
      invoiceNumber: [''],
      email: [''],
      type: [0],
      paymentStatus: [0],
    });
    this.superAdminService.popupClosed$.subscribe(refresh => {
      if (refresh) {
        this.fetchData(this.searchFilterData);
      }
    });
    this.fetchData(this.searchFilterData);
    this.scrollToTop();
    this.selectPaymentStatus = [
      { name: 'All', value: 0 },
      { name: 'Pending', value: 1 },
      { name: 'Payment Failed', value: 2 },
    ];

    this.selectType = [
      { name: 'All', value: 0 },
      { name: 'Booking', value: 1 },
      { name: 'Deferred Invoice', value: 2 },
      { name: CUSTOM_INVOICE, value: 3 },
    ];
  }

  revenueDashboardBreadcrumbs(): void {
    const rolePath = this.role === USER_ROLES.SUPER_ADMIN ? 'super-admin' : 'manager';
    const breadcrumbObj: BreadcrumbItem[] = [
      {
        title: 'Parking Statistics',
        url: `${rolePath}/dashboard/${this.companyId}`
      },
      {
        title: 'Revenue Collection List',
      },
    ];
    this.companyService.setAdminHeaders('0', breadcrumbObj);
  }

  markAsPaidFunc(bookingId: string) {
    let request = {
      bookingId: bookingId,
      timeZone: Utility.getLocalTimeZone(),
    };
    this.bookingFlowService.createBookingPaymentOffline(request)
    .pipe(
      autoUnsubWith(this.destroyRef)
    )
    .subscribe({
      next: (_response: any) => {
        if (_response) {
          this.fetchData(this.searchFilterData);
          this.toastService.showToastMessage(
            'success',
            'Success',
            'Booking has been marked as paid.'
          );
        }
        
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error?.error?.message
        );
        
      },
    });
  }

  markAsPaidForCustomInvoice(invoiceId: string) {
    let request = {
      invoiceId: invoiceId,
      timeZone: Utility.getLocalTimeZone(),
    };
    this.sharedDriverService.createInvoicePaymentOffline(request)
    .pipe(
      autoUnsubWith(this.destroyRef)
    )
    .subscribe({
      next: (_response: any) => {
        if (_response) {
          this.fetchData(this.searchFilterData);
          this.toastService.showToastMessage(
            'success',
            'Success',
            'Custom invoice has been marked as paid.'
          );
        }
        
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error?.error?.message
        );
        
      },
    });
  }

  showData(rowData,event) {
    const actionMap = {
      pay: () => {
        this.dialogControlService.openDialog(PayRepayModalComponent, {
          styleClass: 'pay-repay',
          data: {
            ...(rowData.bookingType === CUSTOM_INVOICE
              ? { invoiceId: rowData.bookingId }
              : { id: rowData.bookingId }),
            driverId:rowData.driverId,
            bookingType:rowData.type !== CUSTOM_INVOICE
          },
        });
        this.dialogControlService.onClose().subscribe(() => {
          this.updateBookingStateAfterPay();
      });
      },
      markAsPaid: (rowData, event) => {
        this.confirmationService.confirm({
          target: event.target as EventTarget,
          message: MARK_AS_PAID_CONFIRMATION,
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            if (rowData.type === CUSTOM_INVOICE) {
              this.markAsPaidForCustomInvoice(rowData.bookingId);
            } else {
              this.markAsPaidFunc(rowData.bookingId);
            }
          },
        });
      },
    };
    this.items = [
      {
        items: [
          {
            label: '',
            icon: '',
            escape: false,
          },
          {
            label: rowData.paymentStatus === 'Payment Failed' ? 'Re-Pay' : 'Pay',
            icon: 'common-icon custom-pay',
            visible: !(rowData.paymentStatusEnum === this.paymentStatusForMessage.RecurringPendingPayment),
            command: () => {
              actionMap.pay();
            },
          },
          {
            label: 'Mark as Paid',
            icon: 'common-icon mark-as-paid',
            escape: false,
            visible: (rowData?.paymentStatus === 'Payment Failed' ||
              rowData?.paymentStatus === 'Pending'),
            command: () => {
              actionMap.markAsPaid(rowData, event)
            },
          },
          {
            label: this.getInvoiceLabel(rowData),
            icon: 'common-icon create-invoice',
            escape: false,
            visible: this.shouldShowInvoiceAction(rowData), // Dynamically control visibility
            command: () => {
              this.redirectToCustomInvoice(rowData);
            },
          },
        ],
      },
    ];
  }
  sendPaymentReminder(): void {
    const bookingAndInvoiceIds = this.selectedProducts.map(
      (item) => item.bookingId || item.invoiceId
    );
    this.sendPaymentReminderApiCall(bookingAndInvoiceIds);
  }

  sendPaymentReminderApiCall(bookingAndInvoiceIds: string[]): void {
    const requestPayload = {
      bookingAndInvoiceIds: bookingAndInvoiceIds,
      timezone: Utility.getLocalTimeZone(),
    };

    this.superAdminService
      .sendPaymentReminder(requestPayload)
      .pipe(
        autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.handleReminderSuccess(response);
        },
        error: (error) => {
          this.handleReminderError(error);
        },
      });
  }

  private handleReminderSuccess(response: any): void {
    if (response.message === 'Reminder send successfully') {
      this.toastService.showToastMessage(
        'success',
        'Success',
        'Reminder sent successfully'
      );
      this.selectedProducts = [];
      this.fetchData(this.searchFilterData);
    }
  }

  private handleReminderError(error): void {
    this.toastService.showToastMessage(
      'error',
      'Error',
      error.error.message
    );
  }

  shouldShowInvoiceAction(rowData: any): boolean {
    // Hide the action for 'Custom Invoices'
    if (rowData.type === CUSTOM_INVOICE) {
      return false;
    }
    // Check the Last Payment Date
    if (rowData.lastDateForPayment) {
      const currentDate = new Date();
      const paymentDate = new Date(rowData.lastDateForPayment);
      const diffInDays = Math.floor(
        (paymentDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays <= 5) {
        return true;
      }
    }
    return false;
  }

  getInvoiceLabel(rowData: any): string {
    if (rowData.type === 'Booking') {
      return 'Generate Invoice';
    } else if (rowData.type === 'Deferred Invoice') {
      return 'Re-Generate Invoice';
    }
    return '';
  }

  getPaymentDateClass(lastDateForPayment: string | null): string {
    if (!lastDateForPayment) return ''; // No class if the date is missing

    const currentDate = new Date();
    const paymentDate = new Date(lastDateForPayment);
    const diffInDays = Math.floor(
      (paymentDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays <= 5) {
      return 'pstatus-payment-failed';
    } else if (diffInDays > 5 && diffInDays <= 15) {
      return ' pstatus-Refunded';
    } else if (diffInDays > 15) {
      return 'pstatus-pending';
    }
    return ''; // Fallback class
  }

  private updateBookingStateAfterPay(): void {
    this.fetchData(this.searchFilterData);
  }

  redirectToCustomInvoice(rowData) {
    const route =
      this.role == USER_ROLES.SUPER_ADMIN
        ? 'super-admin/invoice'
        : 'manager/invoice';
    const stateData = {
      email: rowData.email,
      parkingLotId: this.parkingLotId,
      driverId: rowData.driverId,
    };

    if (this.role === USER_ROLES.SUPER_ADMIN) {
      stateData['companyId'] = this.companyId;
      this.router.navigate([`${route}/${this.companyId}`], {
        state: stateData,
      });
    } else {
      this.router.navigate([route], {
        state: stateData,
      });
    }
  }

  openCancelModal(bookindId, type, typeOfPendingPayment) {
    const bodyText =
      type === CUSTOM_INVOICE
        ? CANCEL_INVOICE_CONFIRMATION
        : CANCEL_BOOKING_CONFIRMATION;
    const id =
      type === CUSTOM_INVOICE
        ? { invoiceId: bookindId }
        : { bookingId: bookindId };
    this.dialogControlService.openDialog(CancelModalComponent, {
      width: '20%',
      data: {
        comingFromInvoice: true,
        ...id,
        headerText: 'Cancel',
        bodyText: bodyText,
        type: typeOfPendingPayment,
      },
    });
    this.dialogControlService.onClose().subscribe((result) => {
      if (result) {
        this.fetchData(this.searchFilterData);
      }
    });
  }

  resetFilter() {
    this.revenueFilterForm.reset({
      name: '',
      invoiceNumber: '',
      email: '',
      type: 0,
      paymentStatus: 0,
    });
    this.pageNumber = 1;
    this.first = 1;
    this.itemsPerPage = 10;
    this.selectedProducts = [];
    this.searchFilterData = this.revenueFilterForm.value;
    this.fetchData(this.searchFilterData);
  }
  applyFilter() {
    this.itemsPerPage = 10;
    this.fetchData(this.searchFilterData);
  }
  backRoute() {
    const companyPath = this.role === USER_ROLES.SUPER_ADMIN ? `/${this.companyIdLocalStroage}` : '';
    this.router.navigate([`${this.roleRoute}/dashboard${companyPath}`]);
  }
    getTimeZoneAbbreviation(timeZone: string): string {
    return CommonFunctions.getTimeZoneAbbreviation(timeZone);
  }

  scrollToTop(): void {
    CommonFunctions.scrollToTop();
  }

  exportData() {
    const request = this.searchFilterData;
    request.startDate = this.startDate;
    request.endDate = this.endDate;
    request.companyId = this.companyId;
    request.parkingLotId = this.parkingLotId;
    request.timeZone = 'Asia/Calcutta';
    request.pageNumber = 0;
    request.pageSize = 0;
    this.superAdminService.getPendingPaymentBookingList(request)
    .pipe(
      autoUnsubWith(this.destroyRef)
    )
    .subscribe({
      next: (res: any) => {
        this.excelFile = res.data.paymnetBookingListOutputs;
        
        this.export();
      },
      error: () => {
        this.excelFile = [];
        
      },
    });
  }

  onSearch() {
    this.first = 1;
    this.pageNumber = 1;
    this.searchFilterData = { ...this.revenueFilterForm.value };
    this.fetchData(this.searchFilterData);
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.pageNumber = event.page + 1;
    CommonFunctions.scrollTopRow('top');
    this.fetchData(this.searchFilterData);
  }
  fetchData(req) {
    
    req.startDate = this.startDate;
    req.endDate = this.endDate;
    req.companyId = this.companyId;
    req.parkingLotId = this.parkingLotId;
    req.timeZone = 'Asia/Calcutta';
    req.pageNumber = this.pageNumber;
    req.pageSize = this.itemsPerPage;
    this.superAdminService.getPendingPaymentBookingList(req)
    .pipe(
      autoUnsubWith(this.destroyRef)
    )
    .subscribe({
      next: (res: any) => {
        this.revenueCollectionData = res.data.paymnetBookingListOutputs;
        this.totalRecords = res.data.totalRecords;
      },
      error: () => {
        this.revenueCollectionData = [];   
      },
    });
  }
  export() {
    if (this.excelFile) {
      const headers = [
        { header: 'S. No', key: 'index', formatter: (value) => value + 1 },
        { header: 'Name', key: 'name', formatter: (value) => value ? value : '-' },
        { header: 'Email', key: 'email', formatter: (value) => value ? value : '-' },
        { header: 'Type', key: 'type', formatter: (value) => value ? value : '-' },
        { header: 'Status', key: 'paymentStatus', formatter: (value) => value ? value : '-' },
        { header: 'Last Date for Payment', key: 'lastDateForPayment', formatter: (value) => value ? value : '-' },
        { header: 'Invoice Number', key: 'invoiceNumber', formatter: (value) => value ? value : '-' },
        { header: 'Amount', key: 'amount', formatter: (value) => value ? value : '-' },
        { header: 'Payment Mode', key: 'bookingPaymentMode', formatter: (value) => value === PaymentMode.Online ? PAYMENT_MODE.ONLINE : value === PaymentMode.Offline ? PAYMENT_MODE.OFFLINE : '' },
        { header: 'Booking Start Date', key: 'bookingStartDate', formatter: (value) => value ? value : '-' },
        { header: 'Booking End Date', key: 'bookingEndDate', formatter: (value) => value ? value : '-' },
        { header: 'Time Zone', key: 'timeZone', formatter: (value) => value ? this.getTimeZoneAbbreviation(value) : '-' },
        { header: 'Start Month', key: 'startMonth', formatter: (value) => value ? value : '-' },
        { header: 'Parking Lot', key: 'parkingLotName', formatter: (value) => value ? value : '-' },
        { header: 'Booking Type', key: 'bookingType', formatter: (value) => value ? value : '-' },
        { header: 'Billing Cycle', key: 'billingCycle', formatter: (value) => value ? value : '-' },
        { header: 'User Type', key: 'userType', formatter: (value) => value ? value : '-' },
        { header: 'Reminder Count', key: 'paymentReminderSendCount', formatter: (value) => value ? value : '-' },
        { header: 'Last Reminder', key: 'reminderSendDate', formatter: (value) => value ? value : '-' },
        { header: 'Reason', key: 'paymentFailureReason', formatter: (value) => value ? value : '-' },

      ]

      // Ensure you map the data to include the index
      const dataWithIndex = this.excelFile.map((item, index) => ({
        ...item,
        index, // Add the index to each row
      }));
      return CommonFunctions.exportToExcel(dataWithIndex, headers, 'revenue-collection-dashboard_');

    }
  }
}